import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/pro-regular-svg-icons';
import { reminderOptOut } from '../../utils/reminderOptOutService';
import '../Register/RegisterStudent.css';
import '../ChangeTime/ChangeTime.css';

/**
 * /reminders/stop?t=<token> — the "Stop them here" link in every lesson
 * reminder (functions/lessonReminders.js).
 *
 * OPENING THE PAGE CHANGES NOTHING. Mail security scanners fetch every link in
 * an email before a person sees it, so a link that unsubscribed on load would
 * quietly unsubscribe families who never asked. The page only looks up the
 * address ('status'); the change is the button. It also offers the way back.
 *
 * Public (a student or parent may have no portal login) and noindex via
 * noindexRoutes. Same card as /change-time.
 */
export default function ReminderStop() {
	const [params] = useSearchParams();
	const token = params.get('t') || '';
	const [status, setStatus] = useState('loading'); // loading | ready | invalid | error
	const [info, setInfo] = useState(null); // { email, stopped }
	const [busy, setBusy] = useState(false);
	const [changed, setChanged] = useState(false);
	const [error, setError] = useState('');

	useEffect(() => {
		let alive = true;
		if (!token) {
			setStatus('invalid');
			return undefined;
		}
		reminderOptOut(token, 'status')
			.then((r) => {
				if (!alive) return;
				if (r && r.ok) {
					setInfo({ email: r.email, stopped: r.stopped });
					setStatus('ready');
				} else {
					setStatus('invalid');
				}
			})
			.catch(() => alive && setStatus('error'));
		return () => {
			alive = false;
		};
	}, [token]);

	const act = async (action) => {
		if (busy) return;
		setBusy(true);
		setError('');
		try {
			const r = await reminderOptOut(token, action);
			if (r && r.ok) {
				setInfo({ email: r.email, stopped: r.stopped });
				setChanged(true);
			} else {
				setStatus('invalid');
			}
		} catch (err) {
			setError("That didn't go through. Please try again, or just reply to any of our emails and we'll take care of it.");
		} finally {
			setBusy(false);
		}
	};

	const shell = (title, body) => (
		<main className='register-page cyv change-time'>
			<Helmet defer={false}>
				<title>{title} | Color Your Voice</title>
				<meta name='robots' content='noindex,nofollow' />
			</Helmet>
			<div className='register-card'>
				<div className='register-inner'>{body}</div>
			</div>
		</main>
	);

	if (status === 'loading') {
		return shell('Lesson reminders', <p className='register-slots-status'>Loading…</p>);
	}
	if (status === 'error') {
		return shell(
			'Lesson reminders',
			<Alert variant='danger' className='register-error'>
				We couldn't load this page.{' '}
				<button type='button' className='register-retry' onClick={() => window.location.reload()}>
					Try again
				</button>
			</Alert>
		);
	}
	if (status === 'invalid' || !info) {
		return shell(
			'Link not valid',
			<div className='register-success'>
				<h1 className='register-success__title'>This link isn't valid.</h1>
				<p className='register-success__body'>
					It may be old or copied incompletely. Reply to any of our emails and we'll stop the
					reminders for you.
				</p>
			</div>
		);
	}

	const { email, stopped } = info;
	return shell(
		stopped ? 'Lesson reminders stopped' : 'Stop lesson reminders',
		<div className='register-success'>
			{changed && <FontAwesomeIcon icon={faCircleCheck} className='register-success__icon' />}
			<h1 className='register-success__title'>
				{stopped
					? changed
						? 'Done. No more lesson reminders.'
						: 'Lesson reminders are off.'
					: changed
					? 'Lesson reminders are back on.'
					: 'Stop lesson reminders?'}
			</h1>
			<p className='register-success__body'>
				{stopped
					? `${email} won't get the email the morning before each lesson. Notices about studio closures will still come through.`
					: `${email} gets an email the morning before each lesson. Notices about studio closures come either way.`}
			</p>
			{error && (
				<Alert variant='danger' className='register-error'>
					{error}
				</Alert>
			)}
			{stopped ? (
				<button type='button' className='btn-cyv-teal' onClick={() => act('resume')} disabled={busy}>
					{busy ? 'One moment…' : 'Turn reminders back on'}
				</button>
			) : (
				<button type='button' className='btn-cyv-teal' onClick={() => act('stop')} disabled={busy}>
					{busy ? 'One moment…' : 'Stop lesson reminders'}
				</button>
			)}
			<p className='register-success__body' style={{ marginTop: 16 }}>
				<Link to='/'>Back to home</Link>
			</p>
		</div>
	);
}
